import assetsBase from '@exodus/assets-base'
import urlJoin from 'url-join'
import createFetchival from '@exodus/fetch/create-fetchival'
import {
  ASSET_TYPES,
  FILTER_LIST_MODES,
  ORDER_TYPES,
  PAYMENT_METHOD_TYPES,
  PROVIDERS,
} from '../constants.js'
import { filterByCountries, omitUndefinedValues } from '../utils.js'
import { ERROR_PAYMENT_METHOD_NOT_SUPPORTED } from '../errors.js'
import { ENABLED_ASSETS, RAMP_API_URL } from './constants.js'
import rampAdapter from './adapter.js'

export * from './constants.js'
export * from './utils.js'
export { default as adapter } from './adapter.js'

const exodusAssets = assetsBase.default || assetsBase

const rampProvider = ({
  apiKey,
  assets: assets_ = ENABLED_ASSETS,
  countries = {},
  countriesMode = FILTER_LIST_MODES.blacklist,
  enabled = true,
  url = RAMP_API_URL,
  fetch: globalFetch = fetch.bind(globalThis), // eslint-disable-line no-undef
  paymentMethodTypes,
} = {}) => {
  if (!apiKey) {
    throw new Error('`apiKey` is required')
  }

  const fetchival = createFetchival({ fetch: globalFetch })
  const apiUrl = urlJoin(url, 'api')
  const fetch_ = fetchival(apiUrl)
  const limits = new Map()

  const getAssets = async ({ fromAsset, ip } = {}) => {
    const assetLimits = new Map()
    const fromMaxSellAmount = Number.MAX_SAFE_INTEGER
    const fromMinSellAmount = 0
    const availableAssets = []
    const availableCurrencies = []
    const fetchAssets = async (fromAsset_ = 'USD') => {
      try {
        return await fetch_('host-api/v2/assets').get(
          omitUndefinedValues({
            currencyCode: fromAsset_,
            hostApiKey: apiKey,
            userIp: ip,
            withDisabled: false,
            withHidden: true,
          })
        )
      } catch (error) {
        if (error.response?.status !== 400) {
          throw error
        }

        const errorBody = await error.response.json()

        if (errorBody.code !== 'SERVICES.HOST_API.FIAT_CURRENCY_NOT_SUPPORTED') {
          throw error
        }

        return fetchAssets()
      }
    }

    const { assets, maxPurchaseAmount, minPurchaseAmount, currencyCode } =
      await fetchAssets(fromAsset)

    for (const asset of assets) {
      const exodusAsset = rampAdapter.fromProviderToExodusAsset(asset.symbol, assets_)

      if (!exodusAsset) {
        continue
      }

      const fromMaxBuyAmount =
        asset.maxPurchaseAmount === -1 ? maxPurchaseAmount : asset.maxPurchaseAmount

      const fromMinBuyAmount =
        asset.minPurchaseAmount === -1 ? minPurchaseAmount : asset.minPurchaseAmount

      const toMaxBuyAmount = fromMaxBuyAmount / asset.price[currencyCode]
      const toMinBuyAmount =
        Number(asset.minPurchaseCryptoAmount) === -1
          ? 0
          : exodusAsset.currency.baseUnit(asset.minPurchaseCryptoAmount).toDefaultNumber()

      assetLimits.set(exodusAsset.name, {
        toMaxBuyAmount,
        toMinBuyAmount,
        toMaxSellAmount: Number.MAX_SAFE_INTEGER,
        toMinSellAmount: 0,
        fromMaxBuyAmount,
        fromMinBuyAmount,
        fromMaxSellAmount,
        fromMinSellAmount,
      })

      availableAssets.push({
        minBuyAmount: toMinBuyAmount,
        maxBuyAmount: toMaxBuyAmount,
        isBuySupported: true,
        isSellSupported: false,
        name: exodusAsset.name,
        price: asset.price,
        type: ASSET_TYPES.crypto,
      })

      availableCurrencies.push(...Object.keys(asset.price))
    }

    const availableCurrencies_ = [...new Set(availableCurrencies)]
      .filter((currency) => !['HKD', 'TJS', 'UAH'].includes(currency))
      .map((currency) => ({
        minSellAmount: fromMinSellAmount,
        maxSellAmount: fromMaxSellAmount,
        isBuySupported: true,
        isSellSupported: false,
        name: currency,
        type: ASSET_TYPES.fiat,
      }))

    if (availableCurrencies_.some(({ name }) => name === fromAsset)) {
      limits.set(fromAsset, assetLimits)
    }

    return [...availableCurrencies_, ...availableAssets]
  }

  const getCountries = async ({ countryCode, regionCode } = Object.create(null)) => {
    const countries_ = await fetch_('host-api/countries').get()
    const formattedCountries = []

    for (const { code, name } of countries_) {
      const countryCode_ = code.toUpperCase()
      const isCountrySupported = !filterByCountries(
        countries,
        countryCode === countryCode_ ? [countryCode, regionCode] : countryCode_,
        {
          mode: countriesMode,
          properties: [ORDER_TYPES.buy],
        }
      )

      if (!isCountrySupported) {
        continue
      }

      formattedCountries.push({
        code: countryCode_,
        isBuySupported: true,
        isSellSupported: false,
        name,
      })
    }

    return formattedCountries
  }

  const getOrder = async (id, { token } = {}) => {
    const encodedId = encodeURIComponent(id)
    const order = await fetch_(`host-api/purchase/${encodedId}`).get({ secret: token })

    return rampAdapter.fromProviderToExodusOrder({ ...order, id }, assets_)
  }

  const getQuote = async ({
    countryCode,
    fromAmount,
    fromAsset,
    paymentMethod = PAYMENT_METHOD_TYPES.card,
    toAmount,
    toAsset,
    type = ORDER_TYPES.buy,
    walletAddress,
  } = {}) => {
    const providerPaymentMethod = rampAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      assets: assets_,
      country: countryCode,
      currency: fromAsset,
      paymentMethodTypes,
    })

    if (type !== ORDER_TYPES.buy) {
      throw new Error('Only `buy` orders are accepted')
    }

    if (!providerPaymentMethod) {
      throw new Error(ERROR_PAYMENT_METHOD_NOT_SUPPORTED.message)
    }

    if (limits.size === 0 || !limits.has(fromAsset)) {
      await getAssets({ fromAsset })
    }

    const asset = exodusAssets[toAsset]
    const fiatLimits = limits.get(fromAsset)
    const assetLimits = fiatLimits.get(toAsset)
    const searchParameters = new URLSearchParams({ hostApiKey: apiKey })
    const quote = await fetch_(`host-api/v3/onramp/quote/all?${searchParameters}`).post(
      omitUndefinedValues({
        cryptoAmount:
          typeof toAmount === 'number'
            ? asset.currency.defaultUnit(toAmount).toBaseString()
            : undefined,
        cryptoAssetSymbol: rampAdapter.fromExodusToProviderAsset(asset.name, assets_),
        fiatCurrency: fromAsset,
        fiatValue: fromAmount,
        userCryptoAddress: walletAddress,
      })
    )

    return rampAdapter.fromProviderToExodusQuote({
      ...assetLimits,
      ...quote,
      fromAsset,
      paymentMethod: providerPaymentMethod,
      toAsset,
      type,
    })
  }

  const getPaymentMethod = (paymentMethod, parameters = Object.create(null)) =>
    rampAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      ...parameters,
      assets: assets_,
      paymentMethodTypes,
    })

  return {
    name: PROVIDERS.ramp,
    enabled,
    getAssets,
    getCountries,
    getOrder,
    getQuote,
    getPaymentMethod,
    orderTypes: [ORDER_TYPES.buy],
  }
}

export default rampProvider
